import {
  COUNTRY_ECOTAX_TYPE,
  ICountryEcotaxType,
  IOrderItem,
  TYRE_IDENTIFIER_FIELD,
} from '@wyz/types';
import { Col, Flex, Row, Text, Title, TyreInfoBox } from '@wyz/ui';
import { getTyreIdentifiers } from '@wyz/utils';
import { multiply, isNil, sum } from 'lodash-es';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { ValueInfoItem } from '~/purchase/components';

type Props = {
  orderItem: IOrderItem;
  customer: IUserCustomer;
  currency: string;
  ecotaxType: ICountryEcotaxType;
  showBf?: boolean;
};
const TyreItem = ({
  currency,
  orderItem,
  customer,
  ecotaxType,
  showBf = true,
}: Props) => {
  const { t } = useI18n();
  const { cdnUrl } = useConfig();
  const locale = useLocale();

  return (
    <>
      <div>
        {orderItem.wheel && (
          <div className='mb-2'>
            <Text
              weight='bold'
              heading='sm'
              color='tertiary'
              className='text-uppercase'
            >
              {t('app.purchase.wcw.detail.set_of', {
                count: orderItem.wheel.set,
              })}{' '}
              {orderItem.wheel.name}
            </Text>
          </div>
        )}
        <Row>
          <Col sm={5}>
            <TyreInfoBox
              tyre={{
                ...orderItem,
                ...(orderItem.wheel
                  ? {
                      // we don't want to showw CAI when it's a wheel order
                      identifierType: TYRE_IDENTIFIER_FIELD.WYZ_REFERENCE,
                      cai: undefined,
                      alternativeReference: orderItem.wheel.reference,
                      constructorReference: undefined,
                    }
                  : getTyreIdentifiers(
                      {
                        cai: orderItem.cai,
                        alternativeReference: orderItem.customerReference,
                        constructorReference: orderItem.customerReference,
                      },
                      customer.settingTemplate?.generalSetting
                        .tyreIdentifierFields ?? undefined,
                    )),
              }}
              showEan={!orderItem.wheel}
              imageBaseUrl={cdnUrl}
              t={t}
            />
          </Col>
          <Col sm={7} className='d-flex align-items-end'>
            <Flex
              className='w-100 space-x-2 space-y-2'
              alignItems='start'
              justifyContent='between'
              wrap='wrap'
            >
              {showBf && !isNil(orderItem.resolvedBf) && (
                <ValueInfoItem
                  as='div'
                  title={<Trans i18nKey='app.common.price.bf' />}
                  align='right'
                >
                  <Text
                    heading='normal'
                    weight='semi-bold'
                    color='secondary'
                    className='mt-2 text-right'
                    id={`bf-${orderItem.ean}`}
                  >
                    {locale.formatters.money(orderItem.resolvedBf, {
                      currency: currency,
                      fallback: '/',
                    })}
                  </Text>
                </ValueInfoItem>
              )}

              {ecotaxType !== COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED && (
                <ValueInfoItem
                  as='div'
                  title={<Trans i18nKey='app.purchase.cart_page.ecotax_fee' />}
                  align='right'
                >
                  <Text
                    heading='normal'
                    weight='semi-bold'
                    className='text-right mt-2'
                    id={`ecotax-${orderItem.ean}`}
                  >
                    {ecotaxType === COUNTRY_ECOTAX_TYPE.INCLUDED_IN_TYRE ? (
                      <Text heading='normal' weight='semi-bold' color='success'>
                        <Trans i18nKey='app.purchase.cart_page.ecotax_included' />
                      </Text>
                    ) : (
                      locale.formatters.money(orderItem.ecoTaxPrice ?? 0, {
                        currency: currency,
                      })
                    )}
                  </Text>
                </ValueInfoItem>
              )}
              <ValueInfoItem
                as='div'
                title={
                  <Trans i18nKey='app.purchase.cart_page.table.unit_price' />
                }
                align='right'
              >
                <Title
                  heading='lg'
                  weight='semi-bold'
                  className='text-right mt-2'
                  id={`unit-price-${orderItem.ean}`}
                >
                  {locale.formatters.money(orderItem.priceCustomer, {
                    currency: currency,
                  })}
                </Title>
                <Text
                  heading='xs'
                  weight='semi-bold'
                  color='secondary-gray'
                  className='text-right'
                >
                  {t('app.purchase.bf_discount', {
                    price: orderItem.bfDiscount ?? 0,
                  })}
                </Text>
              </ValueInfoItem>
              <ValueInfoItem
                as='div'
                title={<Trans i18nKey='app.common.quantity' />}
                align='right'
              >
                <Title
                  heading='lg'
                  weight='bold'
                  className='text-right mt-2'
                  id={`quantity-${orderItem.ean}`}
                >
                  {orderItem.quantity}
                </Title>
              </ValueInfoItem>
              <ValueInfoItem
                as='div'
                title={<Trans i18nKey='app.common.total_price_without_tax' />}
                align='right'
              >
                <Title
                  heading='lg'
                  weight='bold'
                  className='text-right mt-2'
                  id={`item-total-${orderItem.ean}`}
                >
                  {locale.formatters.money(
                    sum([
                      Number(orderItem.excludedTaxesAmount),
                      multiply(
                        Number(orderItem.ecoTaxPrice ?? 0),
                        orderItem.quantity,
                      ),
                    ]),
                    {
                      currency: currency,
                    },
                  )}
                </Title>
              </ValueInfoItem>
            </Flex>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default TyreItem;
